import Vue from 'vue';
import { database, functions } from "@/plugins/firebase";
import moment from "moment";
import store from "@/store/index";
import { NotificationTypes } from "@/enums/NotificationTypes";
import router from "@/router";
import { App } from "@/constants/App";
import { TRANSACTION_CATEGORY } from "@/constants/TransactionCategories";
import _ from "lodash";
const NotificationsCollection = 'notifications';
export const NotificationModule = {
    namespaced: true,
    state: [],
    mutations: {
        store(state, notification) {
            const x = state.find(x => x.id === notification.id);
            if (x) {
                if (_.isEqual(x, notification)) {
                    return;
                }
                state.splice(state.indexOf(x), 1, notification);
                store.dispatch('notification/storeCache');
                return;
            }
            state.push(notification);
            store.dispatch('notification/storeCache');
        },
        update(state, notification) {
            state.find((oldNotification, index) => {
                if (oldNotification.id === notification.id) {
                    Vue.set(state, index, notification);
                }
            });
        },
        destroy(state, notification) {
            state.splice(state.findIndex(item => notification.id === item.id), 1);
        },
        clear(state) {
            state.splice(0, state.length);
        },
    },
    actions: {
        /** A notification is a side effect - it must never break what triggered it. */
        async send(context, data) {
            try {
                await context.dispatch('deliver', data);
            }
            catch (e) {
                console.error('Notification failed', e);
            }
        },
        async deliver(context, data) {
            const instructor = store.getters['instructor/get'](data.instructor);
            if (!instructor) {
                console.warn(`Instructor ${data.instructor} not found, skipping notification`);
                return;
            }
            const channels = instructor.data.notifications || [];
            if (channels.indexOf(`app.${data.urgency}`) > -1) {
                await database.collection(NotificationsCollection).add({
                    instructor: data.instructor,
                    type: data.type ?? NotificationTypes.INFO,
                    text: data.message,
                    urgency: data.urgency ?? 'info',
                    createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
                    readAt: null,
                    link: data.link
                });
                if (instructor.data.devices && instructor.data.devices.length > 0) {
                    const sendMessage = functions.httpsCallable('sendMessage');
                    sendMessage({
                        to: instructor.data.devices,
                        message: data.message,
                        link: data.link ? router.resolve(data.link).href : App.URL,
                    });
                }
            }
            if (channels.indexOf(`email.${data.urgency}`) > -1) {
                const sendMail = functions.httpsCallable('sendMail');
                sendMail({
                    to: instructor.data.email,
                    message: data.message,
                });
            }
            if (channels.indexOf(`phone.${data.urgency}`) > -1
                && instructor.data.phone) {
                if (data.message.length > 70) {
                    console.error(`Notification is message too long for SMS. Expected 70, ${data.message.length} given.`);
                    return;
                }
                let phone = instructor.data.phone.split(' ').join('');
                if (phone.indexOf('+420') < 0) {
                    phone = '+420' + phone;
                }
                const sendSms = functions.httpsCallable('sendSms');
                sendSms({
                    to: phone,
                    message: data.message,
                }).then(response => {
                    if (response.data === 'SMS sent successfully') {
                        const transaction = {
                            id: '',
                            data: {
                                from: 'cash',
                                to: instructor.id,
                                dateTime: moment().format('YYYY-MM-DD HH:mm:ss'),
                                createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
                                value: -2,
                                category: TRANSACTION_CATEGORY.OTHER,
                                description: `Informativní SMS`,
                                deletedAt: null,
                            },
                        };
                        store.dispatch('transaction/store', [transaction]);
                    }
                });
            }
        },
        async read(context, notification) {
            let document = await database.collection(NotificationsCollection).doc(notification.id);
            await document.update({
                readAt: moment().format('YYYY-MM-DD HH:mm:ss'),
            });
        },
        async readBatch(context, notifications) {
            const dateTime = moment().format('YYYY-MM-DD HH:mm:ss');
            const batch = database.batch();
            for (const notification of notifications) {
                notification.data.readAt = dateTime;
                batch.set(database.collection(NotificationsCollection).doc(notification.id), notification.data);
            }
            await batch.commit();
        },
        async destroy(context, notification) {
            await database.collection(NotificationsCollection).doc(notification.id).delete();
        },
        async destroyBatch(context, notifications) {
            const batch = database.batch();
            for (const notification of notifications) {
                batch.delete(database.collection(NotificationsCollection).doc(notification.id));
            }
            await batch.commit();
        },
        async listen(context, userId) {
            context.commit('clear');
            //await context.dispatch('loadCache');
            return database
                .collection(NotificationsCollection)
                .where('instructor', '==', userId)
                .onSnapshot(snapshot => {
                for (let change of snapshot.docChanges()) {
                    switch (change.type) {
                        case 'added':
                            context.commit('store', { id: change.doc.id, data: change.doc.data() });
                            break;
                        case 'modified':
                            context.commit('update', { id: change.doc.id, data: change.doc.data() });
                            context.dispatch('storeCache');
                            break;
                        case 'removed':
                            context.commit('destroy', { id: change.doc.id, data: change.doc.data() });
                            context.dispatch('storeCache');
                            break;
                    }
                }
            });
        },
        async storeCache(context) {
            localStorage.removeItem(NotificationsCollection);
            try {
                localStorage.setItem(NotificationsCollection, JSON.stringify(context.state));
            }
            catch (e) { }
        },
        async loadCache(context) {
            const cache = localStorage.getItem(NotificationsCollection);
            if (!cache) {
                return;
            }
            const notifications = JSON.parse(cache);
            for (const notification of notifications) {
                context.commit('store', { id: notification.id, data: notification.data });
            }
        },
    },
    getters: {
        unread(state) {
            return (id) => state.filter(notification => notification.data.instructor === id
                && notification.data.readAt === null).sort((a, b) => a.data.createdAt < b.data.createdAt ? 1 : -1);
        },
        all(state) {
            return (id) => state
                .filter(notification => notification.data.instructor === id)
                .sort((a, b) => a.data.createdAt < b.data.createdAt ? 1 : -1);
        },
    }
};
